import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Paper,
  Slider,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ImageIcon from '@mui/icons-material/Image';
import RefreshIcon from '@mui/icons-material/Refresh';
import SaveIcon from '@mui/icons-material/Save';
import SearchIcon from '@mui/icons-material/Search';

import { API_BASE_URL } from '../../config';
import {
  clearWordPressQuoteBackground,
  fetchWordPressQuoteBackgrounds,
  generateWordPressQuoteBackground,
  saveWordPressQuoteBackground,
  searchWordPressQuoteBackgroundAssets,
} from './service';
import type {
  WordPressQuoteBackgroundAdminItem,
  WordPressQuoteBackgroundAssetSummary,
  WordPressRecipeCrop,
} from './types';

const QUOTE_TARGET = { width: 1225, height: 406 };
const QUOTE_ASPECT = QUOTE_TARGET.width / QUOTE_TARGET.height;
const LIST_STATUS_CHIP_SX = {
  height: 20,
  borderRadius: '10px',
  '& .MuiChip-label': {
    px: 0.75,
    fontSize: '0.68rem',
    fontWeight: 500,
    whiteSpace: 'nowrap',
  },
} as const;

function clamp(value: number, min: number, max: number) {
  return Math.min(Math.max(value, min), max);
}

function toAbsoluteUrl(relativeUrl?: string | null) {
  if (!relativeUrl) return '';
  if (/^https?:\/\//i.test(relativeUrl)) return relativeUrl;
  return `${API_BASE_URL}${relativeUrl}`;
}

function toCacheBustedUrl(relativeUrl?: string | null, cacheBustKey?: string | null) {
  const absoluteUrl = toAbsoluteUrl(relativeUrl);
  if (!absoluteUrl) return '';
  if (!cacheBustKey) return absoluteUrl;
  const separator = absoluteUrl.includes('?') ? '&' : '?';
  return `${absoluteUrl}${separator}v=${encodeURIComponent(cacheBustKey)}`;
}

function createDefaultCrop(referenceWidth: number, referenceHeight: number): WordPressRecipeCrop {
  const safeWidth = Math.max(1, Math.round(referenceWidth || 1));
  const safeHeight = Math.max(1, Math.round(referenceHeight || 1));
  const sourceAspect = safeWidth / safeHeight;

  let width = safeWidth;
  let height = safeHeight;
  if (sourceAspect > QUOTE_ASPECT) {
    width = Math.round(safeHeight * QUOTE_ASPECT);
    height = safeHeight;
  } else {
    width = safeWidth;
    height = Math.round(safeWidth / QUOTE_ASPECT);
  }

  return {
    x: Math.max(0, Math.round((safeWidth - width) / 2)),
    y: Math.max(0, Math.round((safeHeight - height) / 2)),
    width,
    height,
    zoom: 1,
    referenceWidth: safeWidth,
    referenceHeight: safeHeight,
  };
}

function editorStateFromCrop(
  crop: WordPressRecipeCrop | null | undefined,
  referenceWidth: number,
  referenceHeight: number,
) {
  const fallback = createDefaultCrop(referenceWidth, referenceHeight);
  const nextCrop = crop || fallback;
  const refWidth = Math.max(1, nextCrop.referenceWidth || referenceWidth || 1);
  const refHeight = Math.max(1, nextCrop.referenceHeight || referenceHeight || 1);
  const baseScale = Math.max(QUOTE_TARGET.width / refWidth, QUOTE_TARGET.height / refHeight);
  const zoom = clamp(QUOTE_TARGET.width / Math.max(1, baseScale * nextCrop.width), 1, 5);

  return {
    focusX: clamp(((nextCrop.x + nextCrop.width / 2) / refWidth) * 100, 0, 100),
    focusY: clamp(((nextCrop.y + nextCrop.height / 2) / refHeight) * 100, 0, 100),
    zoom,
  };
}

function buildCropFromEditor(
  referenceWidth: number,
  referenceHeight: number,
  focusX: number,
  focusY: number,
  zoom: number,
): WordPressRecipeCrop {
  const refWidth = Math.max(1, Math.round(referenceWidth || 1));
  const refHeight = Math.max(1, Math.round(referenceHeight || 1));
  const baseScale = Math.max(QUOTE_TARGET.width / refWidth, QUOTE_TARGET.height / refHeight);
  const scaledWidth = refWidth * baseScale * zoom;
  const scaledHeight = refHeight * baseScale * zoom;
  const centerX = (focusX / 100) * scaledWidth;
  const centerY = (focusY / 100) * scaledHeight;
  const left = clamp(centerX - QUOTE_TARGET.width / 2, 0, Math.max(0, scaledWidth - QUOTE_TARGET.width));
  const top = clamp(centerY - QUOTE_TARGET.height / 2, 0, Math.max(0, scaledHeight - QUOTE_TARGET.height));

  return {
    x: Math.round(left / (baseScale * zoom)),
    y: Math.round(top / (baseScale * zoom)),
    width: Math.max(1, Math.round(QUOTE_TARGET.width / (baseScale * zoom))),
    height: Math.max(1, Math.round(QUOTE_TARGET.height / (baseScale * zoom))),
    zoom,
    referenceWidth: refWidth,
    referenceHeight: refHeight,
  };
}

function getPreviewPlacement(
  referenceWidth: number,
  referenceHeight: number,
  focusX: number,
  focusY: number,
  zoom: number,
) {
  const safeReferenceWidth = Math.max(1, referenceWidth || 1);
  const safeReferenceHeight = Math.max(1, referenceHeight || 1);
  const baseScale = Math.max(QUOTE_TARGET.width / safeReferenceWidth, QUOTE_TARGET.height / safeReferenceHeight);
  const renderedWidth = (safeReferenceWidth * baseScale * zoom / QUOTE_TARGET.width) * 100;
  const renderedHeight = (safeReferenceHeight * baseScale * zoom / QUOTE_TARGET.height) * 100;
  const left = -clamp((focusX / 100) * renderedWidth - 50, 0, Math.max(0, renderedWidth - 100));
  const top = -clamp((focusY / 100) * renderedHeight - 50, 0, Math.max(0, renderedHeight - 100));

  return {
    width: `${renderedWidth}%`,
    height: `${renderedHeight}%`,
    left: `${left}%`,
    top: `${top}%`,
  };
}

type QuoteBackgroundsTabProps = {
  onMessage?: (message: { severity: 'success' | 'error' | 'info'; text: string } | null) => void;
  onChangeComplete?: () => void;
};

export default function QuoteBackgroundsTab({ onMessage, onChangeComplete }: QuoteBackgroundsTabProps) {
  const [items, setItems] = useState<WordPressQuoteBackgroundAdminItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [busyAction, setBusyAction] = useState<string | null>(null);
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [listSearch, setListSearch] = useState('');
  const [assetSearch, setAssetSearch] = useState('');
  const [assetResults, setAssetResults] = useState<WordPressQuoteBackgroundAssetSummary[]>([]);
  const [assetResultsLoading, setAssetResultsLoading] = useState(false);
  const [fieldLabel, setFieldLabel] = useState('Loizos Quote Background Image');
  const [selectedSourceAsset, setSelectedSourceAsset] = useState<WordPressQuoteBackgroundAssetSummary | null>(null);
  const [altText, setAltText] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [focusX, setFocusX] = useState(50);
  const [focusY, setFocusY] = useState(50);
  const [zoom, setZoom] = useState(1);
  const [referenceSize, setReferenceSize] = useState({ width: 1, height: 1 });

  const refreshItems = useCallback(async (keepSelectionKey?: string | null) => {
    try {
      setLoading(true);
      const response = await fetchWordPressQuoteBackgrounds();
      setItems(response.items);
      setFieldLabel(response.fieldLabel || 'Loizos Quote Background Image');
      const nextSelectedKey = keepSelectionKey
        && response.items.some(item => item.key === keepSelectionKey)
        ? keepSelectionKey
        : (selectedKey && response.items.some(item => item.key === selectedKey) ? selectedKey : response.items[0]?.key || null);
      setSelectedKey(nextSelectedKey);
    } catch (error: any) {
      onMessage?.({ severity: 'error', text: error?.response?.data?.message || error?.message || 'Kunde inte läsa quote bakgrundsbilder.' });
    } finally {
      setLoading(false);
    }
  }, [onMessage, selectedKey]);

  useEffect(() => {
    refreshItems();
  }, [refreshItems]);

  const filteredItems = useMemo(() => {
    const search = listSearch.trim().toLowerCase();
    if (!search) return items;
    return items.filter(item => (
      item.label.toLowerCase().includes(search)
      || item.key.toLowerCase().includes(search)
      || (item.sourceAsset?.title || '').toLowerCase().includes(search)
    ));
  }, [items, listSearch]);

  const selectedItem = useMemo(
    () => items.find(item => item.key === selectedKey) || null,
    [items, selectedKey],
  );

  useEffect(() => {
    if (!selectedItem) return;
    setSelectedSourceAsset(selectedItem.sourceAsset || null);
    setAltText(selectedItem.altText || '');
    setTitle(selectedItem.title || '');
    setDescription(selectedItem.description || '');

    const referenceWidth = selectedItem.sourceAsset?.width || selectedItem.crop?.referenceWidth || 1;
    const referenceHeight = selectedItem.sourceAsset?.height || selectedItem.crop?.referenceHeight || 1;
    setReferenceSize({ width: referenceWidth, height: referenceHeight });
    const nextEditor = editorStateFromCrop(selectedItem.crop || null, referenceWidth, referenceHeight);
    setFocusX(nextEditor.focusX);
    setFocusY(nextEditor.focusY);
    setZoom(nextEditor.zoom);
  }, [selectedItem]);

  const quotePreviewPlacement = useMemo(
    () => getPreviewPlacement(referenceSize.width, referenceSize.height, focusX, focusY, zoom),
    [referenceSize, focusX, focusY, zoom],
  );

  const sourcePreviewUrl = useMemo(() => {
    if (!selectedSourceAsset?.previewUrl) return '';
    return toAbsoluteUrl(selectedSourceAsset.previewUrl);
  }, [selectedSourceAsset]);

  const renditionPreviewUrl = useMemo(() => {
    if (!selectedItem?.rendition?.url) return '';
    return toCacheBustedUrl(selectedItem.rendition.url, selectedItem.updatedAt || selectedItem.rendition.generatedAt || selectedItem.key);
  }, [selectedItem]);

  const loadAssetResults = useCallback(async () => {
    try {
      setAssetResultsLoading(true);
      const results = await searchWordPressQuoteBackgroundAssets(assetSearch);
      setAssetResults(results);
    } catch (error: any) {
      onMessage?.({ severity: 'error', text: error?.response?.data?.message || error?.message || 'Kunde inte söka DAM-bilder för quote-banner.' });
    } finally {
      setAssetResultsLoading(false);
    }
  }, [assetSearch, onMessage]);

  const updateFocusFromPointer = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const nextFocusX = clamp(((event.clientX - rect.left) / rect.width) * 100, 0, 100);
    const nextFocusY = clamp(((event.clientY - rect.top) / rect.height) * 100, 0, 100);
    setFocusX(nextFocusX);
    setFocusY(nextFocusY);
  }, []);

  const currentCrop = useMemo(
    () => buildCropFromEditor(referenceSize.width, referenceSize.height, focusX, focusY, zoom),
    [referenceSize, focusX, focusY, zoom],
  );

  const runAction = useCallback(async (actionKey: string, action: () => Promise<void>) => {
    try {
      setBusyAction(actionKey);
      await action();
    } finally {
      setBusyAction(null);
    }
  }, []);

  const handleSave = useCallback(async () => {
    if (!selectedItem) return;
    await runAction(`save-${selectedItem.key}`, async () => {
      await saveWordPressQuoteBackground({
        key: selectedItem.key,
        label: selectedItem.label,
        isFallback: selectedItem.isFallback,
        sourceAssetId: selectedSourceAsset?._id || null,
        altText,
        title,
        description,
      });
      onMessage?.({ severity: 'success', text: 'Quote-bannerkopplingen sparades.' });
      await refreshItems(selectedItem.key);
      onChangeComplete?.();
    });
  }, [selectedItem, selectedSourceAsset, altText, title, description, runAction, onMessage, refreshItems, onChangeComplete]);

  const handleGenerate = useCallback(async () => {
    if (!selectedItem) return;
    if (!selectedSourceAsset?._id) {
      onMessage?.({ severity: 'error', text: 'Välj en DAM-bild innan du genererar quote-bannern.' });
      return;
    }

    await runAction(`generate-${selectedItem.key}`, async () => {
      await generateWordPressQuoteBackground({
        key: selectedItem.key,
        label: selectedItem.label,
        isFallback: selectedItem.isFallback,
        sourceAssetId: selectedSourceAsset._id,
        crop: currentCrop,
        altText,
        title,
        description,
      });
      onMessage?.({ severity: 'success', text: 'Quote-bannern genererades och sparades.' });
      await refreshItems(selectedItem.key);
      onChangeComplete?.();
    });
  }, [selectedItem, selectedSourceAsset, currentCrop, altText, title, description, runAction, onMessage, refreshItems, onChangeComplete]);

  const handleClear = useCallback(async () => {
    if (!selectedItem) return;
    await runAction(`clear-${selectedItem.key}`, async () => {
      await clearWordPressQuoteBackground(selectedItem.key);
      setSelectedSourceAsset(null);
      setAltText('');
      setTitle('');
      setDescription('');
      onMessage?.({ severity: 'success', text: 'Quote-bannerkopplingen rensades.' });
      await refreshItems(selectedItem.key);
      onChangeComplete?.();
    });
  }, [selectedItem, runAction, onMessage, refreshItems, onChangeComplete]);

  return (
    <Box sx={{ p: 1.5, display: 'flex', flexDirection: 'column', gap: 1, flex: 1, minHeight: 0 }}>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1}>
        <TextField
          value={listSearch}
          onChange={(event) => setListSearch(event.target.value)}
          placeholder={`Sök värden i ${fieldLabel}`}
          size="small"
          fullWidth
          InputProps={{
            startAdornment: <SearchIcon fontSize="small" style={{ marginRight: 8, opacity: 0.65 }} />,
          }}
        />
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={() => refreshItems(selectedKey)}
          disabled={loading || !!busyAction}
        >
          Uppdatera
        </Button>
      </Stack>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', xl: 'minmax(320px, 0.8fr) minmax(0, 1.2fr)' }, gap: 2, flex: 1, minHeight: 0 }}>
        <Paper variant="outlined" sx={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
          <Box sx={{ p: 1.25, pb: 0.75 }}>
            <Typography variant="subtitle1">Quote bakgrundsbilder</Typography>
            <Typography variant="body2" color="text.secondary">
              En banner per värde i `{fieldLabel}` plus en global fallback.
            </Typography>
          </Box>
          <Divider />
          <Stack spacing={0.5} sx={{ p: 0.75, overflowY: 'auto', flex: 1, minHeight: 0 }}>
            {loading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                <CircularProgress />
              </Box>
            )}
            {!loading && filteredItems.length === 0 && (
              <Alert severity="info">Inga quote-värden matchade sökningen.</Alert>
            )}
            {!loading && filteredItems.map(item => {
              const selected = item.key === selectedKey;
              const visibleProductCount = item.isFallback ? item.resolvedProductCount : item.productCount;
              const secondaryLabel = !item.isFallback && item.key.trim().toLowerCase() !== item.label.trim().toLowerCase()
                ? item.key
                : '';
              return (
                <Box
                  key={item.key}
                  onClick={() => setSelectedKey(item.key)}
                  sx={{
                    border: '1px solid',
                    borderColor: selected ? 'primary.main' : 'divider',
                    borderRadius: 1,
                    p: 0.75,
                    cursor: 'pointer',
                    backgroundColor: selected ? 'action.selected' : 'background.paper',
                  }}
                >
                  <Stack spacing={0.4}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                      <Typography variant="subtitle2" sx={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', lineHeight: 1.25 }}>
                        {item.label}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
                        {visibleProductCount} produkter
                      </Typography>
                    </Box>
                    {secondaryLabel && (
                      <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.2 }}>
                        {secondaryLabel}
                      </Typography>
                    )}
                    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                      {item.isFallback && <Chip size="small" label="Fallback" color="default" sx={LIST_STATUS_CHIP_SX} />}
                      {item.hasRendition ? (
                        <Chip size="small" label="Klar" color="success" sx={LIST_STATUS_CHIP_SX} />
                      ) : (
                        <Chip size="small" label="Saknas" color="warning" sx={LIST_STATUS_CHIP_SX} />
                      )}
                      {item.sourceAssetUpdated && (
                        <Chip size="small" label="Källbild uppdaterad" color="warning" variant="outlined" sx={LIST_STATUS_CHIP_SX} />
                      )}
                      {!item.isFallback && item.fallbackProductCount > 0 && (
                        <Chip size="small" label={`Fallback används (${item.fallbackProductCount})`} color="info" variant="outlined" sx={LIST_STATUS_CHIP_SX} />
                      )}
                      {item.isOrphaned && (
                        <Chip size="small" label="Orphaned" color="default" variant="outlined" sx={LIST_STATUS_CHIP_SX} />
                      )}
                    </Stack>
                  </Stack>
                </Box>
              );
            })}
          </Stack>
        </Paper>

        <Paper variant="outlined" sx={{ p: 2, overflowY: 'auto', minWidth: 0 }}>
          {!selectedItem && !loading && (
            <Alert severity="info">Välj ett quote-värde i listan för att koppla bild, beskära och spara bannern.</Alert>
          )}

          {selectedItem && (
            <Stack spacing={2}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, alignItems: 'flex-start' }}>
                <Box>
                  <Typography variant="h6">{selectedItem.label}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {selectedItem.isFallback ? 'Global fallback-banner' : `Värde: ${selectedItem.key}`}
                  </Typography>
                </Box>
                <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                  {selectedItem.isFallback && <Chip label="Fallback" color="default" />}
                  {selectedItem.hasRendition ? (
                    <Chip label="Banner sparad" color="success" />
                  ) : (
                    <Chip label="Banner saknas" color="warning" />
                  )}
                </Stack>
              </Box>

              {(selectedItem.sourceAssetUpdated || (!selectedItem.hasRendition && selectedItem.resolvedProductCount > 0)) && (
                <Alert severity={selectedItem.sourceAssetUpdated ? 'warning' : 'info'}>
                  {selectedItem.sourceAssetUpdated
                    ? 'Källbilden har ändrats sedan bannern genererades. Generera om och publicera sedan ändringarna för berörda produkter.'
                    : 'Den här bannern används av produkter men saknar sparad rendition. Välj bild, generera och publicera sedan ändringarna för berörda produkter.'}
                </Alert>
              )}

              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', xl: 'minmax(0, 1.05fr) minmax(320px, 0.95fr)' }, gap: 2 }}>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="subtitle1" sx={{ mb: 1 }}>Beskärning 1225 x 406</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                    Sök fram en DAM-bild, välj den och justera sedan fokus och zoom tills bannern sitter rätt.
                  </Typography>

                  <Box
                    onPointerDown={updateFocusFromPointer}
                    onPointerMove={event => {
                      if (event.buttons === 1) updateFocusFromPointer(event);
                    }}
                    sx={{
                      position: 'relative',
                      width: '100%',
                      aspectRatio: `${QUOTE_TARGET.width} / ${QUOTE_TARGET.height}`,
                      overflow: 'hidden',
                      borderRadius: 1,
                      background: 'linear-gradient(135deg, #eceff1, #f8fafc)',
                      border: '1px solid',
                      borderColor: 'divider',
                      cursor: 'grab',
                    }}
                  >
                    {sourcePreviewUrl ? (
                      <Box
                        component="img"
                        src={sourcePreviewUrl}
                        alt={selectedSourceAsset?.title || selectedItem.label}
                        onLoad={event => {
                          const element = event.currentTarget;
                          if (element.naturalWidth > 0 && element.naturalHeight > 0) {
                            setReferenceSize({
                              width: selectedSourceAsset?.width || element.naturalWidth,
                              height: selectedSourceAsset?.height || element.naturalHeight,
                            });
                          }
                        }}
                        sx={{
                          position: 'absolute',
                          width: quotePreviewPlacement.width,
                          height: quotePreviewPlacement.height,
                          left: quotePreviewPlacement.left,
                          top: quotePreviewPlacement.top,
                          objectFit: 'fill',
                          userSelect: 'none',
                          pointerEvents: 'none',
                        }}
                      />
                    ) : (
                      <Stack alignItems="center" justifyContent="center" sx={{ height: '100%', color: 'text.secondary' }}>
                        <ImageIcon />
                        <Typography variant="body2">Välj en DAM-bild för att börja</Typography>
                      </Stack>
                    )}
                    <Box sx={{ position: 'absolute', inset: 0, border: '2px solid rgba(255,255,255,0.75)', boxShadow: 'inset 0 0 0 1px rgba(7,47,83,0.28)' }} />
                  </Box>

                  <Stack spacing={1.5} sx={{ mt: 2 }}>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Zoom</Typography>
                      <Slider min={1} max={5} step={0.05} value={zoom} onChange={(_event, nextValue) => setZoom(Number(nextValue))} />
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Horisontell position</Typography>
                      <Slider min={0} max={100} step={1} value={focusX} onChange={(_event, nextValue) => setFocusX(Number(nextValue))} />
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Vertikal position</Typography>
                      <Slider min={0} max={100} step={1} value={focusY} onChange={(_event, nextValue) => setFocusY(Number(nextValue))} />
                    </Box>
                  </Stack>

                  <Divider sx={{ my: 2 }} />

                  <Stack direction={{ xs: 'column', md: 'row' }} spacing={1}>
                    <TextField
                      value={assetSearch}
                      onChange={(event) => setAssetSearch(event.target.value)}
                      placeholder="Sök DAM-bilder"
                      size="small"
                      fullWidth
                    />
                    <Button
                      variant="outlined"
                      startIcon={<SearchIcon />}
                      onClick={loadAssetResults}
                      disabled={assetResultsLoading}
                    >
                      Sök
                    </Button>
                  </Stack>

                  <Stack spacing={0.75} sx={{ mt: 1.5, maxHeight: 260, overflowY: 'auto' }}>
                    {assetResultsLoading && (
                      <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                        <CircularProgress size={24} />
                      </Box>
                    )}
                    {!assetResultsLoading && assetResults.length === 0 && (
                      <Typography variant="body2" color="text.secondary">
                        Sök i DAM för att välja källbild.
                      </Typography>
                    )}
                    {!assetResultsLoading && assetResults.map(asset => (
                      <Box
                        key={asset._id}
                        onClick={() => {
                          setSelectedSourceAsset(asset);
                          const nextWidth = asset.width || referenceSize.width || 1;
                          const nextHeight = asset.height || referenceSize.height || 1;
                          setReferenceSize({ width: nextWidth, height: nextHeight });
                          const nextEditor = editorStateFromCrop(null, nextWidth, nextHeight);
                          setFocusX(nextEditor.focusX);
                          setFocusY(nextEditor.focusY);
                          setZoom(nextEditor.zoom);
                        }}
                        sx={{
                          border: '1px solid',
                          borderColor: selectedSourceAsset?._id === asset._id ? 'primary.main' : 'divider',
                          borderRadius: 1,
                          p: 1,
                          cursor: 'pointer',
                          backgroundColor: selectedSourceAsset?._id === asset._id ? 'action.selected' : 'background.paper',
                        }}
                      >
                        <Box sx={{ display: 'grid', gridTemplateColumns: '64px 1fr', gap: 1 }}>
                          <Box sx={{ width: 64, height: 64, borderRadius: 1, overflow: 'hidden', bgcolor: 'common.white', border: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 0.5 }}>
                            {asset.previewUrl ? (
                              <Box component="img" src={toAbsoluteUrl(asset.previewUrl)} alt={asset.title} sx={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                            ) : (
                              <ImageIcon color="disabled" />
                            )}
                          </Box>
                          <Stack spacing={0.5} minWidth={0}>
                            <Typography variant="subtitle2" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {asset.title}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">{asset.fileName}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {asset.width && asset.height ? `${asset.width}x${asset.height} px` : 'Okänd upplösning'}
                            </Typography>
                          </Stack>
                        </Box>
                      </Box>
                    ))}
                  </Stack>
                </Paper>

                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="subtitle1" sx={{ mb: 1 }}>Sparad banner</Typography>
                  <Box
                    sx={{
                      width: '100%',
                      aspectRatio: `${QUOTE_TARGET.width} / ${QUOTE_TARGET.height}`,
                      borderRadius: 1,
                      overflow: 'hidden',
                      bgcolor: 'grey.100',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mb: 2,
                    }}
                  >
                    {renditionPreviewUrl ? (
                      <Box component="img" src={renditionPreviewUrl} alt={selectedItem.label} sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    ) : (
                      <Typography variant="body2" color="text.secondary">Ingen sparad quote-banner ännu</Typography>
                    )}
                  </Box>

                  {selectedSourceAsset && (
                    <Paper variant="outlined" sx={{ p: 1.25, mb: 2, bgcolor: 'grey.50' }}>
                      <Typography variant="subtitle2">Vald källbild</Typography>
                      <Typography variant="body2">{selectedSourceAsset.title}</Typography>
                      <Typography variant="caption" color="text.secondary">{selectedSourceAsset.fileName}</Typography>
                    </Paper>
                  )}

                  <Stack spacing={1.5}>
                    <TextField
                      label="Alt text"
                      size="small"
                      value={altText}
                      onChange={(event) => setAltText(event.target.value)}
                      fullWidth
                    />
                    <TextField
                      label="Title"
                      size="small"
                      value={title}
                      onChange={(event) => setTitle(event.target.value)}
                      fullWidth
                    />
                    <TextField
                      label="Description"
                      size="small"
                      value={description}
                      onChange={(event) => setDescription(event.target.value)}
                      fullWidth
                      multiline
                      minRows={3}
                    />
                  </Stack>

                  <Divider sx={{ my: 2 }} />

                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} flexWrap="wrap" useFlexGap>
                    <Button
                      variant="outlined"
                      startIcon={<SaveIcon />}
                      onClick={handleSave}
                      disabled={!!busyAction}
                    >
                      Spara koppling
                    </Button>
                    <Button
                      variant="contained"
                      startIcon={<ImageIcon />}
                      onClick={handleGenerate}
                      disabled={!!busyAction}
                    >
                      {selectedItem.hasRendition ? 'Generera om banner' : 'Generera banner'}
                    </Button>
                    <Button
                      variant="text"
                      color="inherit"
                      startIcon={<DeleteOutlineIcon />}
                      onClick={handleClear}
                      disabled={!!busyAction}
                    >
                      Rensa
                    </Button>
                  </Stack>
                </Paper>
              </Box>
            </Stack>
          )}
        </Paper>
      </Box>
    </Box>
  );
}
